import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { ILoginData } from '@app/lib/models/login.model';
import { ICaseItem, ICaseSearchRequestBody, ISearchRequestParams } from '../models/case-assignment.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { ListOfFilesService } from '../services/list-of-files.service';
import { MatDialog } from '@angular/material/dialog';
import { ListOfFileItem, ListOfFilesResponse } from '@app/lib/models/list-of-files.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, switchMap } from 'rxjs';
import { IResponse } from '@app/lib/models/response.model';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { IAttachmentDialogData } from '@app/lib/models/dialog-data.model';
import { AttachmentViewerComponent } from '@app/lib/components/attachment-viewer/attachment-viewer.component';
import { DialogService } from '@app/lib/services/dialog.service';
import { ECaseCategory, ECourtId } from '@app/dashboard/models/constant';
import { CaseNumberSearchComponent } from '../widget/case-number-search/case-number-search.component';

@Component({
  selector: 'app-list-of-files',
  standalone: true,
  imports: [BreadcrumbComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTableModule,
    MatInputModule,
    MatSortModule,
    CaseNumberSearchComponent
  ],
  templateUrl: './list-of-files.component.html',
  styleUrl: './list-of-files.component.scss'
})


export class ListOfFilesComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;
  navigationData: any;

  loading: boolean = false;
  userType: string = "";
  columnDefinitions: any[] = [];
  listofFiles: ListOfFileItem[] = [];
  courtData: ILoginData | null = null;
  displayedColumns: string[] = [];
  courtIdData: number | null = null;
  public caseListLoader: WritableSignal<boolean> = signal(false);
  public caseList: WritableSignal<ICaseItem[]> = signal([]);
  public fileDataSource: MatTableDataSource<ListOfFileItem> = new MatTableDataSource();
  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'File Management' },
    { label: 'List Of Files' },
  ]);
  public searchFormGrp: FormGroup = new FormGroup({
    caseNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  constructor(
    private service: ListOfFilesService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private dialogService: DialogService,
  ) {
    this.searchbyCaseNoChange();

  }


  ngOnInit(): void {
    this.listofFiles = [];
    this.fileDataSource = new MatTableDataSource();
    // Subscribe to query params (e.g., ?caseNumber=12345)
    this.route.queryParams.subscribe((params) => {
      const caseNumber = params['caseNumber'];
      if (caseNumber) {
        this.searchFormGrp.patchValue({ caseNumber });
        this.searchHandler(caseNumber); // ✅ Use caseNumber from queryParams
      }
    });

    this.loadCourtData(); // Always load court data after component init
    this.configureSortingAccessor();
  }

  private configureSortingAccessor() {
    this.fileDataSource.sortingDataAccessor = (item: ListOfFileItem, property: string) => {
      switch (property) {
        case 'documentId': return item.docId;
        case 'documentName': return item.docName;
        case 'documentTypeName': return item.docTypeName;
        case 'documentStatus': return (item.docStatus || '').toLowerCase() === 'valid' ? 0 : 1;
        case 'dateReceived': return item.uploadedDate;
        default: return (item as any)[property];
      }
    };
  }
  private searchbyCaseNoChange() {
    this.searchFormGrp.controls['caseNumber'].valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(val => {
          if (typeof val === 'string' && val.trim().length > 3) {
            this.caseListLoader.set(true);
            return true;
          }
          this.caseListLoader.set(false);
          return false;
        }),
        debounceTime(500),
        switchMap((searchVal: string) => this.searchCases(searchVal))
      )
      .subscribe({
        next: (res: IResponse<ICaseItem[]>) => {
          this.caseList.set(res.data);
          this.caseListLoader.set(false);
        },
        error: () => {
          this.caseListLoader.set(false);
        }
      });
  }


  get isAppeal(): boolean {
    return (
      this.courtIdData === ECourtId.SC_ID ||
      this.courtIdData === ECourtId.COA_ID
    );
  }

  setTableHeaders(): void {
    this.columnDefinitions = [
      { column: 'documentId', labels: { lawyer: 'Document Id', admin: 'Document Id' }, show: true },
      { column: 'documentName', labels: { lawyer: 'Document Name', admin: 'Document Name' }, show: true },
      { column: 'documentTypeName', labels: { lawyer: 'Document Type Name', admin: 'Document Type Name' }, show: true },
      { column: 'documentStatus', labels: { lawyer: 'Document Status', admin: 'Document Status' }, show: true },
      { column: 'dateReceived', labels: { lawyer: 'Date Received', admin: 'Date Received' }, show: true },
    ];

    // Filter only columns that should be shown
    this.displayedColumns = this.columnDefinitions
      .filter(col => col.show)
      .map(col => col.column);
  }
  public onCaseSearchOptionSelected(item: any): void {
    const caseNo = item?.caseNumber || item?.courtCaseNumber || '';
    if (caseNo) {
      this.searchFormGrp.patchValue({ caseNumber: caseNo });
    }
  }
  private searchCases(val: string) {
    const body: ICaseSearchRequestBody = {
      caseNumber: val,
      dateOfFiling: 'null'
    };
    const params: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'id',
      direction: 'ASC'
    }
    return this.service.searchCaseNumber(body, params);
  }
  public caseDisplayFn(item: ICaseItem | string) {
    return typeof item === 'object' ? item?.caseNumber : item;
  }

  loadCourtData(): void {
    this.courtData = this.getCourtNameData();
    //console.log('Loaded Court Data:', this.courtData);
    this.userType = this.courtData?.courtId === 1 || this.courtData?.courtId === 2 ? 'admin' : 'lawyer'; // Change to 'admin', 'judge', etc.

    this.setTableHeaders();
  }
  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }

  }

  public searchHandler(caseNumber?: string) {
    this.listofFiles = [];
    this.fileDataSource.data = []; // Reset data instead of re-instantiating
    const formCaseNumber = this.searchFormGrp.value?.caseNumber;
    const targetCaseNumber = caseNumber || formCaseNumber;

    // 2. Validate: Execute if targetCaseNumber has a truthy value OR form is valid
    if (targetCaseNumber || this.searchFormGrp.valid) {
      const reqBody = {
        caseNumber: targetCaseNumber
      };
      // Reset the form and state


      this.service.searchCaseDetailsIDR(reqBody).subscribe({
        next: (res: ListOfFileItem[] | null) => {
          if (res && res.length !== 0) {
            this.listofFiles = res;

            // Default sort for initial display: VALID first, others (INVALID) last
            res.sort((a, b) => {
              const statusA = (a.docStatus || '').toLowerCase();
              const statusB = (b.docStatus || '').toLowerCase();
              if (statusA === 'valid' && statusB !== 'valid') return -1;
              if (statusA !== 'valid' && statusB === 'valid') return 1;
              return 0;
            });

            this.fileDataSource.data = res;
            setTimeout(() => this.setupSorting());
          } else {
            this.listofFiles = [];
            this.fileDataSource.data = [];
            this.dialogService.openDialog1({
              data: {
                title: "Information",
                message: 'No Data Exists',
                icon: 'info', // ✅ Default icon if not provided
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
          }
          this.loading = false
        },
        error: (err) => {
          console.error('Error fetching case data:', err);
          this.loading = false;
        },
      });




    }
    else {
      this.dialogService.openDialog1({
        data: {
          title: "Information",
          message: 'Please select or Enter Case Number',
          icon: 'info', // ✅ Default icon if not provided
        }, disableClose: true,           // Prevents closing on outside click or ESC
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });
    }
  }
  public resetHandler() {
    this.searchFormGrp.reset();
    this.listofFiles = [];
    this.fileDataSource.data = [];
  }
  onPrint(): void {
    const data: IAttachmentDialogData = {
      path: 'files/print-reciept.htm',
      queryParams: { caseNumber: this.searchFormGrp.value.caseNumber, caseCategory: '' }
    };

    this.dialog.open(AttachmentViewerComponent, {
      panelClass: 'dialog-pdf-viewer',
      data, disableClose: true,           // Prevents closing on outside click or ESC
      hasBackdrop: true,            // Shows backdrop to block interaction
      backdropClass: 'dialog-backdrop', // Optional: custom styling
    });
    // if(this.listofFiles.length!==0){
    //   const reqData:any={
    //     caseNumber: this.searchFormGrp.value.caseNumber

    //     }
    //     console.log(`ReqData :: ${reqData}`);
    //     this.service.getPrint(reqData).subscribe({
    //       next: (blob) => {
    //         const url = window.URL.createObjectURL(blob);
    //         window.open(url, '_blank');
    //       },
    //       error: (err) => {
    //         console.error('Error while printing:', err);
    //         this.dialogService.openDialog1( {
    //           data: {
    //             title: "Information",
    //             message: 'Failed to generate print file.',
    //             icon: 'info', // ✅ Default icon if not provided
    //           }
    //         });

    //       },
    //     });

    // }
  }
  getColumnStyle(column: string): any {
    const widths: { [key: string]: string } = {
      documentId: '15%',
      documentName: '27%',
      documentTypeName: '28%',
      documentStatus: '15%',
      dateReceived: '15%',
    };

    return {
      width: widths[column] || 'auto',
      'white-space': 'normal',
      'word-break': 'break-word',
      padding: '4px 2px',
      'text-align': column === 'select' ? 'center' : 'left'
    };
  }
  onDocumentClick(file: ListOfFileItem): void {
    const data: IAttachmentDialogData = {
      path: 'documents/download.htm',
      queryParams: { fileRef: file.docURL }
    };

    this.dialog.open(AttachmentViewerComponent, {
      panelClass: 'dialog-pdf-viewer',
      data, disableClose: true,           // Prevents closing on outside click or ESC
      hasBackdrop: true,            // Shows backdrop to block interaction
      backdropClass: 'dialog-backdrop', // Optional: custom styling
    });
  }

  private setupSorting() {
    if (this.sort) {
      this.fileDataSource.sort = this.sort;
      this.sort.active = 'documentStatus';
      this.sort.direction = 'asc';
      this.sort.sortChange.emit({ active: 'documentStatus', direction: 'asc' });
      this.fileDataSource.sort.sort({ id: 'documentStatus', start: 'asc', disableClear: false });
    }
  }

}
